/**
 * The map's points arrive as columns (GET /api/listings/map?compact=1,
 * routes/listings.py encode_map_compact, 2026-10-20) and leave here as the
 * same ListingMapPoint objects the object form carried, so the map, the
 * plot squares, the bounded merge and the prime cache never see the
 * difference. The object form (a backend that predates the flag) passes
 * through untouched.
 *
 * Why: the object form was ~185 bytes a point (3.7 MB of JSON for 20k
 * pins) for a phone to parse whole; the columns are ~28 bytes a point.
 */
import type { ListingMapPoint, ListingsMapResponse } from "@/types";

/** The zoom the map's first request asks for: the city view it opens at
 *  (BAKU_VIEWPORT). The page shell's early request (primeListingsMap)
 *  and the map's own first call send the same, so they share one answer. */
export const MAP_OPEN_Z = 11;
/** The answer is the same from this zoom on (stacks and pins, no
 *  bubbles): the map asks with at most this zoom (routes/listings.py
 *  MAP_Z_MAX). */
export const MAP_Z_MAX = 17;

/** Version 1 of the columns; encode_map_compact documents each field. */
export interface CompactMapPayload {
  total: number;
  returned: number;
  cap: number;
  truncated: boolean;
  v: 1;
  /** Source names, indexed by `s`. */
  src: string[];
  /** Verdict tiers, indexed by `t`; tiers[0] is null (no verdict). */
  tiers: (string | null)[];
  /** Coordinate origin in 1e-5 degree units. */
  base: [number, number];
  /** Each distinct coordinate once (offsets from `base`), in first-use order. */
  lat: number[];
  lng: number[];
  /** Per point: 0 = the next new coordinate, k = coordinate k-1. */
  c: number[];
  /** Per point: source index, or -1 for the next [id, external_id] of `x`. */
  s: number[];
  /** Per point: a numeric external id as the difference from the previous
   *  numeric one, any other id as its string. */
  e: (number | string | null)[];
  t: number[];
  /** Overpriced % x 10, null when there is no verdict. */
  o: (number | null)[];
  /** 1 = approximate placement (drawn hollow). */
  a: number[];
  /** [point index, plot size in sot], in point order. */
  sot: [number, number][];
  x: [string | null, string | null][];
  /** A thinned answer: the zoom, and its busy squares as columns
   *  (coordinates as offsets from `base`, `w` = listings in the square). */
  z?: number;
  /** `k`: 1 = a stack (every listing on one coordinate). */
  g?: { lat: number[]; lng: number[]; w: number[]; k?: number[] };
}

/** Either answer of GET /api/listings/map, as ListingsMapResponse. */
export function decodeMapPoints(body: ListingsMapResponse | CompactMapPayload): ListingsMapResponse {
  if (body && Array.isArray((body as ListingsMapResponse).points)) return body as ListingsMapResponse;
  const b = body as CompactMapPayload;
  if (!b || b.v !== 1) throw new Error("listings/map: unknown payload");
  const n = b.c.length;
  for (const col of [b.s, b.e, b.t, b.o, b.a]) {
    if (col.length !== n) throw new Error("listings/map: ragged payload");
  }
  const g = b.g ?? { lat: [], lng: [], w: [] };
  if (g.lng.length !== g.lat.length || g.w.length !== g.lat.length) throw new Error("listings/map: ragged payload");
  const [baseLat, baseLng] = b.base;
  const points: ListingMapPoint[] = new Array(n + g.lat.length);
  let next = 0; // the next new coordinate
  let prevId = 0; // the last numeric external id
  let odd = 0; // the next pair of x
  let plot = 0; // the next entry of sot
  for (let i = 0; i < n; i++) {
    const ci = b.c[i];
    const k = ci === 0 ? next++ : ci - 1;
    let id: string | null;
    let externalId: string | null;
    const s = b.s[i];
    if (s < 0) {
      [id, externalId] = b.x[odd++];
    } else {
      const e = b.e[i];
      if (typeof e === "number") {
        prevId += e;
        externalId = String(prevId);
      } else {
        externalId = e;
      }
      id = `${b.src[s]}:${externalId}`;
    }
    const o = b.o[i];
    let sot: number | null = null;
    if (plot < b.sot.length && b.sot[plot][0] === i) sot = b.sot[plot++][1];
    // The object form's keys, in its order.
    points[i] = {
      id,
      external_id: externalId,
      latitude: (baseLat + b.lat[k]) / 1e5,
      longitude: (baseLng + b.lng[k]) / 1e5,
      // Not sent: nothing on the map reads it.
      price: null,
      ai_verdict_tier: b.tiers[b.t[i]] ?? null,
      ai_overpriced_pct: o == null ? null : o / 10,
      approx: b.a[i] === 1,
      sot,
    };
  }
  if (next !== b.lat.length || next !== b.lng.length) throw new Error("listings/map: ragged payload");
  for (let j = 0; j < g.lat.length; j++) {
    points[n + j] = {
      id: null,
      external_id: null,
      latitude: (baseLat + g.lat[j]) / 1e5,
      longitude: (baseLng + g.lng[j]) / 1e5,
      price: null,
      ai_verdict_tier: null,
      ai_overpriced_pct: null,
      approx: false,
      sot: null,
      w: g.w[j],
      stack: g.k?.[j] === 1,
    };
  }
  return { total: b.total, returned: b.returned, cap: b.cap, truncated: b.truncated, points, thin: b.z ?? null };
}
